import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import {
  usePageBreadcrumbs,
  usePageTitle,
} from "@redotech/redo-web/page-header-slot";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { RedoHorizontalTabsWithActions } from "@redotech/redo-web/tabs-with-actions";
import { memo, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RecordViewCatalogSource,
  useRecordViewCatalog,
} from "../views/use-record-view-catalog";
import { selectedViewTab, ViewTabLink } from "./selected-view-tab";

const NO_ALIASES: readonly string[] = [];
const NO_TABS: readonly ViewTab[] = [];

export interface ViewTab extends ViewTabLink {
  label: string;
  count?: number;
}

/**
 * One record type's views as tabs under the page title: All, built-ins and
 * saved views from the catalog, then the type's other tables.
 */
export const RecordViewTabs = memo(function RecordViewTabs({
  source,
  allAliases = NO_ALIASES,
  otherTabs = NO_TABS,
  title,
  section,
  createViewHref,
}: {
  source?: RecordViewCatalogSource;
  /** Paths besides the All view's that show every record, such as the index. */
  allAliases?: readonly string[];
  otherTabs?: readonly ViewTab[];
  title: string;
  /** The breadcrumb after Home; the selected tab's label when unset. */
  section?: string;
  createViewHref?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [group] = useRecordViewCatalog(source ? [source] : []);

  const tabs = useMemo(
    (): ViewTab[] => [
      ...(group?.views ?? []).map((view, index) => ({
        key: view.key,
        href: view.href,
        label: view.label,
        count: view.count,
        aliases: index === 0 ? allAliases : undefined,
      })),
      ...otherTabs,
    ],
    [group, allAliases, otherTabs],
  );
  const selected = selectedViewTab(tabs, pathname, search);

  if (!selected) {
    return null;
  }

  const hrefByKey = new Map(tabs.map((tab) => [tab.key, tab.href]));
  return (
    <>
      <ViewTabsHeader crumb={section ?? selected.label} title={title} />
      <TabsPortal>
        <RedoHorizontalTabsWithActions
          actions={
            createViewHref && (
              <RedoButton
                aria-label={t("Create view")}
                data-dd-action-name="record_view_tabs:create_view_click"
                dataTestId="record-view-tabs-create-view"
                hierarchy="tertiary"
                IconLeading={PlusIcon}
                onClick={() => navigate(createViewHref)}
                size="sm"
              />
            )
          }
          align="center"
          tabs={
            <RedoHorizontalTabs
              drawBottomLine={false}
              pl="none"
              selectedTab={selected.key}
              setSelectedTab={(key) => {
                const href = hrefByKey.get(key);
                if (href) {
                  navigate(href);
                }
              }}
              tabs={tabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
                count: tab.count,
                dataTestId: `record-view-tab-${tab.key}`,
              }))}
            />
          }
        />
      </TabsPortal>
    </>
  );
});

/** Only mounted on a tab's table, so other pages keep their own title and trail. */
const ViewTabsHeader = memo(function ViewTabsHeader({
  title,
  crumb,
}: {
  title: string;
  crumb: string;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  usePageTitle(title);
  const breadcrumbs = useMemo(
    () => [
      { id: "home", name: t("Home"), url: routes.returnsHref },
      { id: "view", name: crumb, url: null },
    ],
    [crumb, t, routes.returnsHref],
  );
  usePageBreadcrumbs(breadcrumbs);
  return null;
});
